import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import {
  Button,
  DeletionModal,
  ResponsiveTable,
  TabGroup,
  ToggleCloudControl,
  Typography
} from 'procyon-ui'
import React, { useMemo, useState, useRef } from 'react'
import { useHistory, useParams } from 'react-router'
import { getServiceAccountName } from 'Utils/PureHelperFuctions'
import { FullScreenModal, SearchInput } from 'V2Components'
import { getHTMLDataTestID } from 'infra/utils'
import { ApiProvider, useDispatch } from 'Core'
import { serviceAccountsSlice } from 'infra/redux/reducers'
import { enqueueNotification } from 'Utils/Helpers'
import { awsColumns, azureColumns, gcpColumns } from './Components'
import CredentialsHelp from './Components/CredentialsHelp'
import AddServiceAccount from './Components/AddServiceAccount'
import useAppView from 'Core/Hooks/useAppView'

const ServiceAccounts = () => {
  const history = useHistory()
  const { appView } = useAppView()
  const dispatch = useDispatch()
  const apiProviderInstance = useRef(new ApiProvider('serviceaccounts'))

  const [searchKey, setSearchKey] = useState('')
  const [toDelete, setToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [credentials, setCredentials] = useState(null)
  const [showAddAccountModal, setAddAccountModal] = useState(false)

  const { accountType } = useParams()
  const { slices } = useMultiSlice(['serviceAccounts', 'accountList', 'projects'])

  const onCloudTypeChange = (v) => history.push(`/${appView}/service-accounts/${v.toLowerCase()}`)

  // geneate row data
  const getRowData = useMemo(() => {
    const targetType = accountType?.toUpperCase()

    const projectLookup = new Map(slices.projects.map((p) => [p.ObjectMeta.ID, p.Spec.DisplayName]))
    const accountLookup = new Map(
      slices.accountList.map((a) => [a.ObjectMeta.ID, a.ObjectMeta.Name])
    )
    const specKeyMap = { AWS: 'AwsSpec', AZURE: 'AzureSpec', GCP: 'GcpSpec' }

    return slices.serviceAccounts
      .filter((sa) => sa?.Spec?.Type?.toUpperCase() === targetType)
      .map((serviceAccount) => {
        const { Spec, Status, ObjectMeta, ProcyonInternal } = serviceAccount
        const currentSpec = Spec?.[specKeyMap[Spec?.Type]]

        const hasCloudDetails =
          Status?.GcpStatus?.Email ||
          Status?.AzureStatus?.DisplayName ||
          Status?.AwsStatus?.UserName
        const hasError = Status?.Status?.Status === '0'
        const isLoading = !hasCloudDetails && !hasError

        return {
          ID: ObjectMeta?.ID,
          Name: Spec?.GcpSpec?.Name,
          Type: Spec?.Type,
          Project: projectLookup.get(Spec?.GcpSpec?.Project?.RefID),
          Account: accountLookup.get(currentSpec?.Account?.RefID),
          Email: Status?.GcpStatus?.Email,
          UserName: Status?.AwsStatus?.UserName,
          Arn: Status?.AwsStatus?.Arn,
          DisplayName: Status?.AzureStatus?.DisplayName,
          ServicePrincipalId: Status?.AzureStatus?.ServicePrincipalId,
          AppId: Status?.AzureStatus?.AppId,
          AppObjectId: Status?.AzureStatus?.AppObjectId,
          internal: ProcyonInternal,
          error: {
            error: Status?.Status?.Error,
            hasError: hasError,
            loading: isLoading
          },
          delete: {
            onClick: () => setToDelete(serviceAccount)
          },
          credentials: {
            onClick: () => setCredentials(serviceAccount),
            visible: Status?.PrivateKeyData?.Elems?.[0]
          }
        }
      })
  }, [slices, accountType])

  const filteredData = useMemo(() => {
    if (!searchKey) return getRowData
    const lowerKey = searchKey.toLowerCase()

    return getRowData.filter((item) => {
      return (
        // check Name Gcp
        (item.Name && item.Name.toLowerCase().includes(lowerKey)) ||
        // check UserName Aws
        (item.UserName && item.UserName.toLowerCase().includes(lowerKey)) ||
        // check DisplayName Azure
        (item.DisplayName && item.DisplayName.toLowerCase().includes(lowerKey)) ||
        // check Email
        (item.Email && item.Email.toLowerCase().includes(lowerKey)) ||
        // check Account
        (item.Account && item.Account.toLowerCase().includes(lowerKey))
      )
    })
  }, [getRowData, searchKey])

  const currentColumns = useMemo(() => {
    switch (accountType) {
      case 'aws':
        return awsColumns()
      case 'gcp':
        return gcpColumns()
      case 'azure':
        return azureColumns()
      default:
        return []
    }
  }, [accountType])

  const handleDeleteConfirm = async () => {
    if (!toDelete) return
    setIsDeleting(true)
    try {
      await apiProviderInstance.current.setInstance(toDelete).delete()
      await dispatch(serviceAccountsSlice.thunk())
      enqueueNotification('Service account deleted successfully', 'info')
      setToDelete(null)
    } catch (e) {
      enqueueNotification('Failed to delete service account', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  const deleteText = useMemo(() => {
    if (!toDelete) return ''
    return `Are you sure you want to delete ${getServiceAccountName(toDelete)} Service Account?`
  }, [toDelete])

  const privateKey = useMemo(() => {
    if (!credentials) return ''
    return credentials.Status?.PrivateKeyData?.Elems?.[0]
  }, [credentials])

  return (
    <div>
      <div className='flex justify-between items-center'>
        <Typography variant='h3'>Service Accounts</Typography>
        <div className='flex gap-2 items-center'>
          <ToggleCloudControl
            ariaLabel='cloud-select'
            onChange={(_, v) => v && onCloudTypeChange(v)}
            options={['AWS', 'GCP', 'AZURE']}
            value={accountType ? accountType.toUpperCase() : 'AWS'}
          />
          <SearchInput sx={{ width: '280px' }} searchKey={searchKey} onChange={setSearchKey} />
          <Button
            data-testid={getHTMLDataTestID('service-accounts', 'button', 'create')}
            onClick={() => setAddAccountModal(true)}
            size='md'
            style={{
              width: '200px'
            }}
            variant='primary'
            icon={faPlus}
          >
            Add Service Account
          </Button>
        </div>
      </div>

      <div className='mt-8 bg-[var(--color-common-bg-base)] p-4 h-screen'>
        {accountType === 'aws' ? (
          <ResponsiveTable scrollX='1200px' data={filteredData} columns={currentColumns} />
        ) : (
          <TabGroup
            tabs={[
              {
                label: "User's Service Accounts",
                tabContent: (
                  <ResponsiveTable
                    scrollX='1200px'
                    data={filteredData.filter((x) => !x.internal)}
                    columns={currentColumns}
                  />
                )
              },
              {
                label: 'Internal Service Accounts',
                tabContent: (
                  <ResponsiveTable
                    scrollX='1200px'
                    data={filteredData.filter((x) => x.internal)}
                    columns={currentColumns}
                  />
                )
              }
            ]}
          />
        )}
      </div>

      {/* ADD Servie Account Modal  */}
      {showAddAccountModal && (
        <AddServiceAccount open={true} onClose={() => setAddAccountModal(false)} />
      )}

      {/* Delete Modal */}
      <FullScreenModal showModal={!!toDelete}>
        <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
          <DeletionModal
            style={{
              width: '650px'
            }}
            message='This action cannot be undone.'
            onClickCancel={() => setToDelete(null)}
            onClickDelete={handleDeleteConfirm}
            title={deleteText}
          />
        </div>
      </FullScreenModal>

      {/* Credential Modal */}
      <FullScreenModal showModal={!!credentials}>
        <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
          <div className='w-[626px] rounded-lg p-8 bg-white'>
            <CredentialsHelp privateKey={privateKey} onClose={() => setCredentials(null)} />
          </div>
        </div>
      </FullScreenModal>
    </div>
  )
}

export { ServiceAccounts }
